import {
  IThingSpaceGraph,
  ThingSpaceType,
  ISkillGroupPopularityModel,
} from "@skills-app/models/thingspaceGraph";
import { ThingSpaceApiActions, ThingSpacePageActions } from "../actions";
import {
  createReducer,
  on,
  createSelector,
  createFeatureSelector,
} from "@ngrx/store";
import { ICapability } from "@skills-app/models/capability";

export interface IThingSpaceState {
  thingSpaceRevisions: IThingSpaceGraph[];
  currentThingSpaceType: ThingSpaceType | null;
  currentThingSpaceDraft: IThingSpaceGraph | null;
  SkillPopularitiesData: ISkillGroupPopularityModel;
  isLoading: IThingSpaceLoading;
  editorDirty: boolean;
  capability: ICapability | null;
}

export interface IThingSpaceLoading {
  graphSkill: boolean;
  graphText: boolean;
  graphDraft: boolean;
  revisions: boolean;
}

const initialState: IThingSpaceState = {
  thingSpaceRevisions: [],
  currentThingSpaceType: null,
  currentThingSpaceDraft: null,
  SkillPopularitiesData: {
    PopularityBySignificantGroup: {},
  },
  isLoading: {
    graphSkill: false,
    graphText: false,
    graphDraft: false,
    revisions: false,
  },
  editorDirty: false,
  capability: null,
};

export const reducer = createReducer(
  initialState,

  on(ThingSpaceApiActions.populateRevisions, (state, action) => ({
    ...state,
    thingSpaceRevisions: action.revisions,
    isLoading: { ...state.isLoading, revisions: false },
  })),

  on(ThingSpacePageActions.switchThingSpaceType, (state, action) => ({
    ...state,
    currentThingSpaceType: action.thingSpaceType,
    isLoading: { ...state.isLoading, revisions: true },
  })),

  on(ThingSpaceApiActions.populateDraft, (state, action) => ({
    ...state,
    currentThingSpaceDraft: action.draft,
  })),

  on(ThingSpacePageActions.clearDraft, (state) => ({
    ...state,
    currentThingSpaceDraft: null,
  })),

  on(ThingSpaceApiActions.populateSkillPopularities, (state, action) => ({
    ...state,
    SkillPopularitiesData: action.SkillPopularities,
  })),

  on(ThingSpacePageActions.requestSaveTextBox, (state) => ({
    ...state,
    editorDirty: false,
    isLoading: { ...state.isLoading, graphText: true },
  })),

  on(ThingSpaceApiActions.upsertedRevision, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, graphText: false },
  })),

  on(ThingSpacePageActions.requestLoadEditPage, (state, action) => ({
    ...state,
    currentThingSpaceType: action.thingSpaceType,
    isLoading: { ...state.isLoading, graphDraft: true },
  })),

  on(ThingSpaceApiActions.loadedEditPage, (state) => ({
    ...state,
    isLoading: { ...state.isLoading, graphDraft: false },
  })),

  on(
    ThingSpacePageActions.requestUpsertGraphSkill,
    ThingSpacePageActions.requestDeleteGraphSkill,
    (state) => ({
      ...state,
      isLoading: { ...state.isLoading, graphSkill: true },
    }),
  ),

  on(
    ThingSpaceApiActions.graphSkillAdded,
    ThingSpaceApiActions.graphSkillDeleted,
    (state) => ({
      ...state,
      isLoading: { ...state.isLoading, graphSkill: false },
    }),
  ),

  on(ThingSpacePageActions.editorDirty, (state) => ({
    ...state,
    editorDirty: true,
  })),

  on(ThingSpaceApiActions.populateCapability, (state, action) => ({
    ...state,
    capability: action.capability,
  })),
);

export const getThingSpaceState =
  createFeatureSelector<IThingSpaceState>("thingSpace");

export const getAllRevisions = createSelector(
  getThingSpaceState,
  (state) => state.thingSpaceRevisions,
);

export const getCurrentType = createSelector(
  getThingSpaceState,
  (state) => state.currentThingSpaceType,
);

export const getCurrentDraft = createSelector(
  getThingSpaceState,
  (state) => state.currentThingSpaceDraft,
);

export const getCurrentDraftText = createSelector(
  getThingSpaceState,
  (state) => state.currentThingSpaceDraft?.Text ?? "",
);

export const getSkillPopularitiesData = createSelector(
  getThingSpaceState,
  (state) => state.SkillPopularitiesData,
);

export const getDraftLoading = createSelector(
  getThingSpaceState,
  (state) => state.isLoading.graphDraft,
);

export const getGraphSkillLoading = createSelector(
  getThingSpaceState,
  (state) => state.isLoading.graphSkill,
);

export const getGraphTextLoading = createSelector(
  getThingSpaceState,
  (state) => state.isLoading.graphText,
);

export const getRevisionsLoading = createSelector(
  getThingSpaceState,
  (state) => state.isLoading.revisions,
);

export const getThingSpaceLoading = createSelector(
  getThingSpaceState,
  (state) => state.isLoading,
);

export const getEditorStatus = createSelector(
  getThingSpaceState,
  (state) => state.editorDirty,
);

export const getCapability = createSelector(
  getThingSpaceState,
  (state) => state.capability,
);
